import { useState, useEffect, useRef } from "react";
import { z } from "zod";
import { createFileRoute, useNavigate, redirect } from "@tanstack/react-router";
import { canCreateRfq } from "@/lib/roles";
import { currentUserQueryOptions } from "@/hooks/useCurrentUser";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useCreateRfqFromIndent, useRfqLookups } from "@/features/rfqs/api";
import { getSupabase } from "@/integrations/external-supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useIndents } from "@/features/indents/api";
import { resolveIndentPreselect } from "@/features/rfqs/preselect";

import { useBranch } from "@/context/BranchContext";

const searchSchema = z.object({
  fromIndent: z.string().optional(),
  templateId: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/rfqs/new")({
  validateSearch: (s: Record<string, unknown>) => ({
    indentId: typeof s.indentId === "string" ? s.indentId : undefined,
    templateId: typeof s.templateId === "string" ? s.templateId : undefined,
  }),
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(currentUserQueryOptions());
    if (!canCreateRfq(me.roles)) throw redirect({ to: "/rfqs" });
  },
  component: NewRfq,
});

function NewRfq() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { currentBranchId } = useBranch();
  const [indentId, setIndentId] = useState<string>("");
  const [templateId, setTemplateId] = useState<string>(search.templateId ?? "__blank");
  const { data: lookups } = useRfqLookups();
  const create = useCreateRfqFromIndent();

  useEffect(() => {
    if (!search.templateId && lookups?.templates && templateId === "__blank") {
      const defaultTpl = lookups.templates.find((t) => t.is_default);
      if (defaultTpl) {
        setTemplateId(defaultTpl.id);
      }
    }
  }, [lookups, search.templateId, templateId]);

  // Fetch approved indents directly from the unified Django backend via useIndents
  const { data: indentOptions, isSuccess: indentsLoaded } = useIndents({ status: "Purchase Approved", branchId: currentBranchId });
  const indents = indentOptions ?? [];

  // ?indentId=<id> (from "Create RFQ" on an approved PI): preselect it once the approved options are loaded
  const preselectHandled = useRef(false);
  useEffect(() => {
    const r = resolveIndentPreselect(search.indentId, indentsLoaded ? indentOptions : undefined, indentId, preselectHandled.current);
    if (r.kind === "none") return;
    preselectHandled.current = true;
    if (r.kind === "select") setIndentId(r.id);
    else toast.error("That Purchase Indent is not approved for purchase or is not available to you. Choose one from the list.");
  }, [search.indentId, indentsLoaded, indentOptions, indentId]);

  const onCreate = async () => {
    if (!indentId) return toast.error("Choose an approved indent");
    try {
      const rfq = await create.mutateAsync({
        indentId,
        templateId: templateId && templateId !== "__blank" ? templateId : null,
      });
      toast.success(`RFQ ${rfq.rfq_number} created`);
      navigate({ to: "/rfqs/$rfqId", params: { rfqId: rfq.id } });
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">New RFQ</h1>
      <Card>
        <CardHeader><CardTitle>Step 1 — Source</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label>Purchase Indent (accepted for purchase) *</Label>
            <Select value={indentId} onValueChange={setIndentId}>
              <SelectTrigger><SelectValue placeholder="Choose an approved indent" /></SelectTrigger>
              <SelectContent>
                {indents.map((i) => (
                  <SelectItem key={i.id} value={i.id}>{i.indent_no} — {i.item_desc}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Quotation Template</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger><SelectValue placeholder="Choose template or Blank" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__blank">Blank (no template)</SelectItem>
                {(lookups?.templates ?? []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? " (Default)" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Template fields and terms are snapshotted onto this RFQ. You can further customize line items and terms on the next step.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate({ to: "/rfqs" })}>Cancel</Button>
        <Button onClick={onCreate} disabled={create.isPending || !indentId}>Create RFQ & Continue</Button>
      </div>
    </div>
  );
}
